import React, { useState, useMemo } from 'react';
import { Persona } from '../../types/persona';
import { PersonaCard } from './PersonaCard';

interface PersonaListProps {
  personas: Persona[];
  onPersonaClick: (persona: Persona) => void;
  loading?: boolean;
}

type SortField = 'name' | 'age' | 'iq' | 'knowledge_specific';
type SortDirection = 'asc' | 'desc';

export const PersonaList: React.FC<PersonaListProps> = ({
  personas,
  onPersonaClick,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrand, setFilterBrand] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Get unique brand relationship types for filter
  const brandRelationshipTypes = useMemo(() => {
    const types = new Set(personas.map((p) => p.brand_relationship.usage_status));
    return Array.from(types).sort();
  }, [personas]);

  // Filter and sort personas
  const filteredPersonas = useMemo(() => {
    let filtered = personas;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.demographics.occupation.toLowerCase().includes(term) ||
          p.demographics.location.toLowerCase().includes(term)
      );
    }

    // Apply brand relationship filter
    if (filterBrand) {
      filtered = filtered.filter((p) => p.brand_relationship.usage_status === filterBrand);
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'name':
          aVal = a.name;
          bVal = b.name;
          break;
        case 'age':
          aVal = a.demographics.age;
          bVal = b.demographics.age;
          break;
        case 'iq':
          aVal = a.cognitive.iq;
          bVal = b.cognitive.iq;
          break;
        case 'knowledge_specific':
          aVal = a.cognitive.knowledge_specific;
          bVal = b.cognitive.knowledge_specific;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return 0;
    });

    return filtered;
  }, [personas, searchTerm, filterBrand, sortField, sortDirection]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gray-300 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-300 rounded w-3/4" />
                <div className="h-3 bg-gray-300 rounded w-1/2" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-3 bg-gray-300 rounded" />
              <div className="h-3 bg-gray-300 rounded w-5/6" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (personas.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No personas</h3>
        <p className="mt-1 text-sm text-gray-500">
          Get started by generating personas for this project.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Search personas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Brand Filter */}
          <div className="sm:w-64">
            <select
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
            >
              <option value="">All Brand Relationships</option>
              {brandRelationshipTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="sm:w-48">
            <select
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={`${sortField}-${sortDirection}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split('-') as [SortField, SortDirection];
                setSortField(field);
                setSortDirection(direction);
              }}
            >
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="age-asc">Age (Low-High)</option>
              <option value="age-desc">Age (High-Low)</option>
              <option value="iq-asc">IQ (Low-High)</option>
              <option value="iq-desc">IQ (High-Low)</option>
              <option value="knowledge_specific-asc">Knowledge (Low-High)</option>
              <option value="knowledge_specific-desc">Knowledge (High-Low)</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-2 text-sm text-gray-500">
          Showing {filteredPersonas.length} of {personas.length} personas
        </div>
      </div>

      {/* Persona Grid */}
      {filteredPersonas.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No personas match your search criteria.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredPersonas.map((persona) => (
            <PersonaCard
              key={persona.personaId}
              persona={persona}
              onClick={() => onPersonaClick(persona)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
